import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";

import { DashboardLayout } from "@layouts/index";

import { PrimaryButton } from "@shared/buttons";
import { Modal } from "@shared/controls/Modal";
import { ListingTypeCard, ListingCardMobile } from "@shared/cards";
import AgentAvailabiltyCal from "@shared/agent-availablity-cal";
import { DataTable, TableColumn } from "@shared/Table/DataTable";
import { Pagination } from "@shared/Table/Pagination";
import { Search } from "@shared/Search";

import {
  deleteProperty,
  getListings,
  toggleVisibility,
} from "@services/krent.service";
import { PropertyListResponse } from "@interfaces/properties.interface";
import { useSessionStorage } from "../../hooks";
import { useLocation } from "react-router";
import ktoast from "@services/toast.service";
import { ToastContainer } from "react-toastify";
import "./listing.css";

export const Listing = () => {
  const location = useLocation();
  const { state }: { state: any } = location;

  const [user] = useSessionStorage("user", {});
  const [showModal, setShowModal] = useState<boolean>(false);
  const [tab, setTab] = useState<string>("shortlet");
  const [modalType, setModalType] = useState<string>("listing");
  const [search, setSearch] = useState<string>("");
  const [id, setId] = useState<any>(0);
  const [listingId, setListingId] = useState<string>("");
  const [deListResponse, setdeListResponse] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [listings, setListings] = useState<PropertyListResponse[]>([]);
  const [intent, setIntent] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [availabilityListing, setAvailabilityListing] =
    useState<PropertyListResponse | null>(null);

  const openAvailabilityModal = (listing: PropertyListResponse) => {
    setAvailabilityListing(listing);
  };

  const closeAvailabilityModal = () => {
    setAvailabilityListing(null);
  };

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get("tab") || "shortlet";

  const TABS = [
    { key: "shortlet", label: "Shortlet" },
    { key: "long lease", label: "Rent" },
    // { key: "sale", label: "Sales" },
  ];

  const columns: TableColumn<PropertyListResponse>[] = [
    {
      key: "title",
      header: "Property name",
      render: (listing) => listing.title,
    },
    {
      key: "price",
      header: "Cost",
      render: (listing) => formatCost(listing.price),
    },
    {
      key: "type",
      header: "Property type",
      render: (listing) => listing?.kind?.title || listing.title,
    },
    {
      key: "createdAt",
      header: "Listed on",
      render: (listing) => formatDate(listing.createdAt),
    },
    {
      key: "status",
      header: "Status",
      render: (listing) => (
        <span
          className={`badge ${
            listing.published ? "badge-published" : "badge-pending"
          }`}
        >
          {listing.published ? "Published" : "Pending"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (listing) => {
        const hasPendingImages =
          listing.otherImages && listing.otherImages.length === 0;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 40,
            }}
          >
            <div className="dropdown">
              <button
                className="btn btn-link p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{
                  color: "#9ca3af",
                  textDecoration: "none",
                  fontSize: "20px",
                }}
              >
                ⋮
              </button>
              <ul className="dropdown-menu fs-14 border-round px-3">
                <li>
                  <Link
                    className="dropdown-item"
                    to={`/property/${listing.slug}`}
                    state={{
                      property: listing,
                      id: listing.slug,
                    }}
                  >
                    View
                  </Link>
                </li>
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li>
                  <span
                    className="dropdown-item cursor-pointer"
                    onClick={() => {
                      setModalType("edit-listing");
                      setId(listing.id);
                      setIntent(listing.intent);
                      setShowModal(true);
                    }}
                  >
                    Edit Property
                  </span>
                </li>
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li>
                  <span
                    className="dropdown-item cursor-pointer"
                    onClick={() => {
                      openAvailabilityModal(listing);
                      setId(listing.id);
                    }}
                  >
                    Edit Availability Calendar
                  </span>
                </li>
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li>
                  <span
                    className="dropdown-item cursor-pointer"
                    onClick={() => {
                      toggleProperty(listing.id);
                    }}
                  >
                    {listing.isVisible ? "Hide" : "Unhide"}
                  </span>
                </li>
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li>
                  <span
                    className="dropdown-item cursor-pointer"
                    onClick={() => {
                      setModalType("delete-listing");
                      setShowModal(true);
                      setId(listing.id);
                      setListingId(listing.id);
                    }}
                  >
                    Delete
                  </span>
                </li>
              </ul>
            </div>
            {hasPendingImages && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <span className="pending-images-chip">Pending images</span>
                <a
                  className="add-now-link text-center mt-1"
                  onClick={() =>
                    navigate(
                      `/listings/${
                        listing.intent === "long lease"
                          ? "rental"
                          : listing.intent === "sale"
                            ? "sales"
                            : listing.intent
                      }/?id=${listing.id}`,
                    )
                  }
                >
                  Add now
                </a>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const filterListings = (
    listings: any[],
    tab: string,
    searchTerm: string = "",
  ) => {
    let filtered = listings?.filter((listing: any) => listing.intent === tab);

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered?.filter(
        (listing: any) =>
          listing.title?.toLowerCase().includes(lowerSearchTerm) ||
          listing?.kind?.title?.toLowerCase().includes(lowerSearchTerm),
      );
    }

    return filtered;
  };

  useEffect(() => {
    if (state && state["showToast"] === true) {
      ktoast.success(state["toastMessage"]);
    }
  }, [state]);
  useEffect(() => {
    document.body.style.overflow = "auto";
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    setTab(activeTab);
  }, [activeTab]);

  // Function to handle tab change and update URL
  const handleTabChange = (newTab: string) => {
    setTab(newTab);
    setCurrentPage(1);
    navigate(`?tab=${encodeURIComponent(newTab)}`, { replace: true });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const formatDate = (dateStr: string | Date) => {
    if (!dateStr) return "-";
    const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatCost = (cost: number) => {
    if (!cost) return "-";
    const formatted = new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(cost);
    return formatted.replace("NGN", "₦");
  };

  // pagination
  const PAGE_SIZE = 10;
  const filteredListings = filterListings(listings, tab, search);
  const totalPages = Math.max(
    1,
    Math.ceil(filteredListings.length / PAGE_SIZE),
  );
  const pagedListings = filteredListings.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const pageNums: (number | "…")[] = [];
  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) pageNums.push(i);
  } else {
    pageNums.push(1, 2, "…", totalPages);
  }
  // useEffect(() => {
  //   setLoading(true);

  //   const abortController = new AbortController();

  //   getListings(user?.id, { intent: tab }, abortController.signal)
  //     .then((res: any) => {
  //       // console.log(res);
  //       setListings(res.data);
  //     })
  //     .catch((e) => {
  //       console.log(e);
  //     })
  //     .finally(() => {
  //       setLoading(false);
  //     });

  //   return () => {
  //     abortController.abort();
  //   };
  // }, [user, tab]);

  const fetchListings = () => {
    setLoading(true);
    const abortController = new AbortController();

    const params: { [key: string]: string } = { intent: tab };
    if (search) {
      params["search"] = search;
    }

    getListings(user?.id, params, abortController.signal)
      .then((res: any) => {
        setListings(res.data);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      abortController.abort();
    };
  };

  useEffect(() => {
    const abort = fetchListings();
    return abort;
  }, [user, tab, search]);

  useEffect(() => {
    const abortController = new AbortController();

    const switchTabs = (event: any) => {
      // console.log(event.target); // newly activated tab
      // console.log(event.target.dataset);
      // console.log(event.relatedTarget) // previous active tab
      getListings(
        user?.id,
        { intent: event.target.dataset.tabIntent },
        abortController.signal,
      )
        .then((res: any) => {
          // console.log(res);
          setListings(res.data);
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          setLoading(false);
        });
    };
    const tabEl = document.querySelectorAll(
      'button[data-bs-toggle="tab"]',
    ) as NodeList;
    // console.log(tabEl);
    tabEl.forEach((el) => {
      el.addEventListener("show.bs.tab", switchTabs);
      // console.log('added');
    });

    return () => {
      abortController.abort();
      tabEl.forEach((el) => {
        el.removeEventListener("show.bs.tab", switchTabs);
        // console.log('removed');
      });
    };
  }, [user]);

  const archiveProperty = (id: string) => {
    const abortController = new AbortController();
    setLoading(true);
    // setShowModal(false);
    deleteProperty(id)
      .then(() => {
        ktoast.error("Listing deleted successfully");
        setdeListResponse("Property Delisted Successfully");
        setShowModal(false);
      })
      .catch((err) => {
        console.log(err);
        ktoast.error("Listing deleted successfully");
        setdeListResponse(err?.errors?.message[0]);
      })
      .finally(() => {
        setLoading(false);

        // setdeListResponse('Property Delisted Successfully');
      });

    getListings(user?.id, { intent: tab }, abortController.signal)
      .then((res: any) => {
        // console.log(res);
        setListings(res.data);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      abortController.abort();
    };
  };
  const toggleProperty = (id: string) => {
    const abortController = new AbortController();
    setLoading(true);
    // setShowModal(false);
    toggleVisibility(id)
      .then((res: any) => {
        if (res?.data?.isVisible) {
          ktoast.success("Listing is now visible");
        } else {
          ktoast.success("Listing hidden successfully");
        }

        setShowModal(false);
      })
      .catch((err) => {
        console.log(err);
        ktoast.error(err?.errors?.message[0]);
      })
      .finally(() => {
        setLoading(false);
      });

    getListings(user?.id, { intent: tab }, abortController.signal)
      .then((res: any) => {
        // console.log(res);
        setListings(res.data);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      abortController.abort();
    };
  };

  return (
    <DashboardLayout
      title="Listing"
      subtitle="Manage all the properties listed on this platform"
    >
      <ToastContainer limit={1} />

      {/* Desktop view */}
      <div className="d-none d-md-block">
        {/* Tab bar */}
        <div className="tab-bar">
          <div className="tabs">
            {TABS.map(({ key, label }) => (
              <button
                key={key}
                className={`tab-btn ${tab === key ? "tab-btn-active" : ""}`}
                onClick={() => handleTabChange(key)}
              >
                {label}
                {tab === key && <span className="tab-underline" />}
              </button>
            ))}
          </div>

          <button
            className="add-btn"
            onClick={() => {
              setModalType("listing");
              setShowModal(true);
            }}
            title="New Listing"
          >
            <i className="fa-solid fa-plus text-white fs-16" />
          </button>
        </div>

        {/* Search + filter */}
        <Search
          placeholder="Search property"
          value={search}
          onChange={setSearch}
          showFilter={true}
        />

        {/* Table */}
        <DataTable
          columns={columns}
          data={pagedListings}
          loading={loading}
          rowKey={(listing) => listing.id}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      <div className="d-block d-md-none p-2">
        <div className="mb-3">
          <input
            type="text"
            className="form-control border-round-xl p-2 px-4 mb-2"
            style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb" }}
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="form-select border-round-xl p-2 px-4 mb-2"
            style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb" }}
            onChange={(e) => handleTabChange(e.target.value)}
          >
            <option defaultValue={tab}>Select Listings</option>
            <option value="shortlet">Short Let</option>
            <option value="long lease">Rent</option>
            <option value="sale">Sale</option>
          </select>
          <PrimaryButton
            type="primary"
            text="New Listing"
            click={() => {
              setModalType("listing");
              setShowModal(true);
            }}
          />
        </div>

        <div className="col-12">
          {filterListings(listings, tab, search)?.map((listing) => {
            return (
              <ListingCardMobile
                propert={listing}
                id={listing.id}
                key={listing.id}
                property={listing.title}
                propertyType={listing?.kind?.title}
                cost={listing.price}
                duration={listing.cycle}
                listed={listing.createdAt}
                status={listing.published}
                intent={listing.intent}
                setIntent={setIntent}
                setModalType={setModalType}
                setShowModal={() => setShowModal(true)}
                setId={setId}
                setListingId={setListingId}
                cardType="listing"
                otherImages={listing.otherImages}
                isVisible={listing.isVisible}
                toggleVisibility={() => toggleProperty(listing.id)}
                onRefresh={fetchListings}
                onEditAvailability={() => openAvailabilityModal(listing)}
              />
            );
          })}
        </div>
      </div>

      <Modal
        id={id?.toString()}
        show={showModal}
        size={modalType === "listing" ? "lg" : "md"}
        onClose={() => {
          setShowModal(false);
          setdeListResponse("");
        }}
      >
        <div className="col-12 px-4">
          {modalType === "listing" && (
            <>
              <h6 className="fw-bold mb-4">Create property listing</h6>
              <ListingTypeCard
                title="Short Let"
                description="Rent out your apartments to people for a short period of time"
                click={() => navigate("/listings/shortlet/")}
              />
              {/* <ListingTypeCard
                title="Rent"
                description="Rent out your apartments to people for longer periods. e.g 6 months"
                click={() => navigate("/listings/rental/")}
              /> */}
              {/* <ListingTypeCard
                title="Sales"
                description="Sell properties or apartments to people"
                click={() => navigate("/listings/sales/")}
              /> */}
              {/* <ListingTypeCard
                title="Krent iBuying"
                description="Sell properties instantly to Krent "
                // click={() => navigate('/listings/iBuying/')}
              /> */}
            </>
          )}
          {modalType === "delisting" && (
            <div className="col">
              <h5 className="fw-bold mb-4">Hide Property</h5>
              <p className="light-text">
                Are you sure you want to Hide this property, it will need to be
                reviewed before it goes live again
              </p>
              <div className="row mt-3">
                <div className="col-7">
                  <PrimaryButton
                    type="primary"
                    text="Delete Property"
                    click={() => {
                      toggleProperty(id);
                      setShowModal(false);
                    }}
                    close
                    bgColor="#EA4335"
                  />
                </div>
                <div className="col-5">
                  <PrimaryButton
                    type="primary"
                    text="Cancel"
                    close
                    click={() => setShowModal(false)}
                  />
                </div>
              </div>
            </div>
          )}
          {modalType === "edit-listing" && (
            <div className="col">
              <h5 className="fw-bold mb-4">
                Are you sure you want to edit a listing that is live
              </h5>
              <p className="light-text">
                Editing properties that are live will take them offline and you
                can resubmit for review when you’re done. Do you want to proceed
              </p>
              <div className="row">
                <div className="col-7">
                  <PrimaryButton
                    type="primary"
                    text="Edit Listing"
                    click={() => {
                      let int;
                      if (intent === "long lease") {
                        int = "rental";
                      } else if (intent === "sale") {
                        int = "sales";
                      } else {
                        int = intent;
                      }
                      navigate(`/listings/${int}/?id=${id}`);
                      // setShowModal(false);
                    }}
                  />
                </div>
                <div className="col-5">
                  <PrimaryButton
                    type="primary"
                    text="Cancel"
                    close
                    click={() => setShowModal(false)}
                  />
                </div>
              </div>
            </div>
          )}
          {modalType === "delete-listing" && (
            <div className="col">
              <h5 className="fw-bold mb-4">Delete Listing</h5>
              {/* {!deListResponse ? ( */}
              <p className="light-text">
                Are you sure you want to delete this listing, this action is
                irreversible
              </p>
              {/* ) : (
                <p>{deListResponse}</p>
              )} */}

              <div className="row">
                {!deListResponse && (
                  <div className="col-7">
                    <PrimaryButton
                      type="primary"
                      text="Delete Listing"
                      click={() => {
                        archiveProperty(listingId);

                        setShowModal(false);
                      }}
                      close
                      bgColor="#EA4335"
                    />
                  </div>
                )}
                <div className={!deListResponse ? "col-5" : "col-12"}>
                  <PrimaryButton
                    type="primary"
                    text={!deListResponse ? "Cancel" : "Close"}
                    close
                    click={() => setShowModal(false)}
                    bgColor={deListResponse ? "#EA4335" : ""}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <AgentAvailabiltyCal
        availabilityModal={!!availabilityListing}
        setAvailabilityModal={(open) => {
          if (!open) closeAvailabilityModal();
        }}
        propertyId={availabilityListing?.id ?? ""}
        normalPrice={availabilityListing?.price ?? 0}
        intent={availabilityListing?.intent}
        availabilityStatus={
          availabilityListing
            ? (availabilityListing as any).availability_status
            : undefined
        }
        onRefresh={fetchListings}
      />
    </DashboardLayout>
  );
};
